/**
 * Boards on Tasks → Settings: who sees each board and who may do what on it
 * (docs/boards-api.md §12 in Gluska-Forge-API).
 *
 * Visibility is `workspace` (the usual rule: General for everyone, a product
 * board for its members and Forge admins) or `members` (its member list only,
 * admins included). Roles run viewer < editor < manager < owner. The API is
 * the gate; this panel only offers what the caller may change: managers and
 * owners edit members, granting roles up to their own (`grantable_roles`),
 * and only owners change visibility. Admins create boards.
 *
 * Archiving (FOR-349) is the recommended way to retire a board: managers and
 * owners can, and it is undone with Unarchive. Deleting is the owner-only
 * danger action, and the API refuses it while the board still has tasks.
 */

import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Archive, LayoutGrid, Plus, Trash2, Globe, Lock, UserLock } from "lucide-react"
import { Link } from "react-router-dom"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge, Card, SButton, Segments } from "@/components/studio/ui"
import { rs } from "@/components/studio/ui-classes"
import { BoardTile } from "@/components/tasks/task-glyphs"
import { ArchiveBoardDialog, ArchivedBoardBadge, BoardActivityList, UnarchiveButton } from "@/components/tasks/board-archive"
import { ARCHIVE_BOARD_EXPLAINER, ARCHIVED_BOARDS_QUERY_KEY, formatArchivedDate } from "@/lib/board-archive"
import { useToast } from "@/hooks/use-toast"
import { useAuth } from "@/hooks/useAuth"
import { api } from "@/lib/api"
import { isAdmin } from "@/lib/roles"
import {
  BOARD_ROLE_HINTS,
  BOARD_ROLE_LABELS,
  boardRoleLevel,
  isBoardArchived,
  type Board,
  type BoardRole,
  type BoardVisibility,
} from "@/lib/task-boards"
import { boardHref } from "@/lib/tasks-nav"
import { cn } from "@/lib/utils"

const VISIBILITY_OPTIONS: { value: BoardVisibility; label: string }[] = [
  { value: "workspace", label: "Workspace" },
  { value: "members", label: "Members only" },
]

const VISIBILITY_HINTS: Record<BoardVisibility, string> = {
  workspace: "Its members and every Forge admin can see it.",
  members: "Only the people listed below can see it, admins included. Nobody else can tell it exists.",
}

/** The API's own words for a refusal (422 field errors, then `error` / `message`). */
function apiError(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: { errors?: Record<string, string[]>; error?: string; message?: string } } })?.response?.data
  const first = data?.errors ? Object.values(data.errors).flat()[0] : undefined
  return first ?? data?.error ?? data?.message ?? fallback
}

/**
 * Who can see a board, as a small icon beside its name wherever boards are
 * listed: a globe for a team board (everyone in Forge), a lock for a private
 * board with several members, a lock-with-person when it is only you.
 * Wrapped in a span so containers that size their direct svg children (the
 * sidebar's menu buttons force 16px) leave it small.
 */
function boardAudience(board: Pick<Board, "visibility" | "member_count" | "is_default">): { kind: "team" | "private" | "only_you"; label: string } {
  if (board.visibility !== "members") {
    return { kind: "team", label: board.is_default ? "Team board: everyone in Forge" : "Team board: its members and every Forge admin" }
  }
  const n = board.member_count ?? 0
  return n <= 1 ? { kind: "only_you", label: "Private: only you" } : { kind: "private", label: `Private: members only (${n})` }
}

export function BoardPrivateMark({ board, className }: { board: Pick<Board, "visibility"> & Partial<Pick<Board, "member_count" | "is_default">>; className?: string }) {
  const { kind, label } = boardAudience({ visibility: board.visibility, member_count: board.member_count, is_default: board.is_default ?? false })
  const Icon = kind === "team" ? Globe : kind === "only_you" ? UserLock : Lock
  return (
    <span title={label} aria-label={label} className={cn("inline-flex shrink-0 items-center", kind === "team" ? "text-muted-foreground/45" : "text-amber-600/90 dark:text-amber-400/90", className)}>
      <Icon aria-hidden className="h-2.5 w-2.5" strokeWidth={2.25} />
    </span>
  )
}

export function BoardsSettingsCard({ boards, isLoading }: { boards: Board[]; isLoading: boolean }) {
  const { user } = useAuth()
  const [editing, setEditing] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const productBoards = boards.filter((b) => !b.is_default)

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <LayoutGrid className="h-4 w-4" /> Boards
        </span>
      }
      actions={
        isAdmin(user) ? (
          <SButton kind="secondary" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> New board
          </SButton>
        ) : undefined
      }
      flush
      className="max-w-3xl"
    >
      {isLoading ? (
        <div className={cn(rs.pulse, "mx-4 mb-4 h-10 md:mx-6")} />
      ) : productBoards.length === 0 ? (
        <p className="px-4 pb-4 text-sm text-muted-foreground md:px-6">You are not on any boards besides General.</p>
      ) : (
        <ul className="pb-2">
          {productBoards.map((board) => (
            <li key={board.key} className={cn("flex items-center gap-3 border-t px-4 py-2.5 md:px-6", rs.rule)}>
              <BoardTile name={board.name} color={board.color} logoUrl={board.logo_url} size={20} />
              <span className="flex min-w-0 flex-1 items-center gap-2 truncate text-sm font-medium">
                <span className="truncate">{board.name}</span>
                <span className="font-mono text-[11px] text-muted-foreground">{board.key}</span>
                <BoardPrivateMark board={board} />
              </span>
              {board.role && <Badge tone="neutral">{BOARD_ROLE_LABELS[board.role]}</Badge>}
              {board.can_manage && (
                <SButton kind="small" onClick={() => setEditing(board.key)}>
                  Settings
                </SButton>
              )}
            </li>
          ))}
        </ul>
      )}

      {editing && <BoardSettingsDialog boardKey={editing} open onOpenChange={(open) => !open && setEditing(null)} />}
      {creating && <NewBoardDialog open onOpenChange={setCreating} />}
    </Card>
  )
}

type Row = { user_id: number; name: string; email: string; role: BoardRole }

function BoardSettingsDialog({ boardKey, open, onOpenChange }: { boardKey: string; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { data: board, isLoading } = useQuery({ queryKey: ["board", boardKey], queryFn: () => api.boards.get(boardKey) })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {board && <BoardTile name={board.name} color={board.color} logoUrl={board.logo_url} size={20} />}
            {board?.name ?? boardKey} settings
            {board && isBoardArchived(board) && <ArchivedBoardBadge label="Archived" />}
          </DialogTitle>
          <DialogDescription>
            {board && isBoardArchived(board)
              ? `${boardKey} is archived. Unarchive it to change its settings or its tasks.`
              : `Who can see ${boardKey}, and what each member may do.`}
          </DialogDescription>
        </DialogHeader>
        {isLoading || !board ? (
          <div className={cn(rs.pulse, "h-40")} />
        ) : isBoardArchived(board) ? (
          <ArchivedBoardSettings board={board} onDone={() => onOpenChange(false)} />
        ) : (
          // Keyed so a refetch after a refused save starts the form from the board as it is.
          <BoardSettingsForm key={JSON.stringify([board.name, board.visibility, board.members])} board={board} onDone={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function BoardSettingsForm({ board, onDone }: { board: Board; onDone: () => void }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { data: usersData } = useQuery({ queryKey: ["users"], queryFn: () => api.users.list() })
  const users = useMemo(() => (Array.isArray(usersData) ? usersData : []), [usersData])

  const [name, setName] = useState(board.name)
  const [color, setColor] = useState(board.color ?? "")
  const [logoUrl, setLogoUrl] = useState(board.logo_url ?? "")
  const [visibility, setVisibility] = useState<BoardVisibility>(board.visibility ?? "workspace")
  const [rows, setRows] = useState<Row[]>(() => (board.members ?? []).map((m) => ({ user_id: m.id, name: m.name, email: m.email, role: m.role })))
  const [adding, setAdding] = useState("")

  const myLevel = boardRoleLevel(board.role)
  const grantable = board.grantable_roles ?? []
  const outsideRank = (role: BoardRole) => boardRoleLevel(role) > myLevel
  const candidates = users.filter((u) => !rows.some((r) => r.user_id === u.id))

  const save = useMutation({
    mutationFn: async () => {
      const changes: { name?: string; visibility?: BoardVisibility; color?: string | null; logo_url?: string | null } = {}
      if (name.trim() && name.trim() !== board.name) changes.name = name.trim()
      if ((color.trim() || null) !== (board.color ?? null)) changes.color = color.trim() || null
      if ((logoUrl.trim() || null) !== (board.logo_url ?? null)) changes.logo_url = logoUrl.trim() || null
      if (board.can_own && visibility !== board.visibility) changes.visibility = visibility

      // Members before visibility: going members-only needs an owner already on the list.
      const before = JSON.stringify((board.members ?? []).map((m) => [m.id, m.role]).sort())
      const after = JSON.stringify(rows.map((r) => [r.user_id, r.role]).sort())
      if (before !== after) {
        await api.boards.syncMembers(board.key, rows.map((r) => ({ user_id: r.user_id, role: r.role })))
      }
      if (Object.keys(changes).length > 0) await api.boards.update(board.key, changes)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["boards"] })
      queryClient.invalidateQueries({ queryKey: ["board", board.key] })
      toast({ title: "Board saved" })
      onDone()
    },
    onError: (e) => {
      queryClient.invalidateQueries({ queryKey: ["board", board.key] })
      toast({ title: "Not saved", description: apiError(e, "The board could not be saved."), variant: "destructive" })
    },
  })

  const addMember = (id: string) => {
    const user = users.find((u) => String(u.id) === id)
    if (!user) return
    const role: BoardRole = grantable.includes("editor") ? "editor" : grantable[0] ?? "viewer"
    setRows((prev) => [...prev, { user_id: user.id, name: user.name, email: user.email, role }])
    setAdding("")
  }

  return (
    <>
      <div className="space-y-6">
        <label className="block space-y-2">
          <span className={rs.label}>Name</span>
          <Input className={rs.input} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
        </label>

        <div className="grid gap-4 sm:grid-cols-[140px_1fr]">
          <label className="block space-y-2">
            <span className={rs.label}>Colour</span>
            <span className="flex items-center gap-2">
              <input
                type="color"
                aria-label="Board colour"
                value={/^#[0-9a-f]{6}$/i.test(color) ? color : "#6B7280"}
                onChange={(e) => setColor(e.target.value.toUpperCase())}
                className="h-9 w-10 cursor-pointer rounded-lg border bg-transparent p-0.5"
              />
              <Input className={rs.input} value={color} placeholder="#3E63DD" maxLength={7} onChange={(e) => setColor(e.target.value)} />
            </span>
          </label>
          <label className="block space-y-2">
            <span className={rs.label}>Logo</span>
            <span className="flex items-center gap-2">
              <BoardTile name={name || board.name} color={/^#[0-9a-f]{6}$/i.test(color) ? color : null} logoUrl={/^https:\/\//i.test(logoUrl) ? logoUrl : null} size={28} />
              <Input className={rs.input} value={logoUrl} placeholder="https://example.com/icon.png" maxLength={500} onChange={(e) => setLogoUrl(e.target.value)} />
            </span>
            <span className="block text-xs text-muted-foreground">A square https image, such as the company's app icon. Empty shows the coloured initial.</span>
          </label>
        </div>

        <div className="space-y-2">
          <span className={rs.label}>Visibility</span>
          {board.can_own ? (
            <Segments label="Visibility" value={visibility} options={VISIBILITY_OPTIONS} onChange={setVisibility} />
          ) : (
            <p className="text-sm">{VISIBILITY_OPTIONS.find((o) => o.value === visibility)?.label}</p>
          )}
          <p className="text-xs text-muted-foreground">
            {VISIBILITY_HINTS[visibility]}
            {!board.can_own && " Only an owner can change this."}
          </p>
        </div>

        <div className="space-y-2">
          <span className={rs.label}>Members</span>
          <ul className={cn("rounded-2xl border", rs.rule)}>
            {rows.length === 0 && <li className="px-3 py-2.5 text-sm text-muted-foreground">No members yet.</li>}
            {rows.map((row) => {
              const locked = outsideRank(row.role)
              return (
                <li key={row.user_id} className={cn("flex items-center gap-2 border-t px-3 py-2 first:border-t-0", rs.rule)}>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{row.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{row.email}</span>
                  </span>
                  <Select
                    value={row.role}
                    disabled={locked}
                    onValueChange={(role) => setRows((prev) => prev.map((r) => (r.user_id === row.user_id ? { ...r, role: role as BoardRole } : r)))}
                  >
                    <SelectTrigger className="h-8 w-[130px] rounded-xl text-sm" aria-label={`Role for ${row.name}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(locked ? [row.role] : grantable).map((role) => (
                        <SelectItem key={role} value={role} title={BOARD_ROLE_HINTS[role]}>
                          {BOARD_ROLE_LABELS[role]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <SButton
                    kind="small"
                    icon
                    aria-label={`Remove ${row.name}`}
                    disabled={locked}
                    onClick={() => setRows((prev) => prev.filter((r) => r.user_id !== row.user_id))}
                  >
                    <Trash2 />
                  </SButton>
                </li>
              )
            })}
          </ul>
          {candidates.length > 0 && (
            <Select value={adding} onValueChange={addMember}>
              <SelectTrigger className="h-8 rounded-xl text-sm" aria-label="Add a member">
                <SelectValue placeholder="Add a member…" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((u) => (
                  <SelectItem key={u.id} value={String(u.id)}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <p className="text-xs text-muted-foreground">
            You can grant roles up to {board.role ? BOARD_ROLE_LABELS[board.role].toLowerCase() : "your own"}. A board keeps at least one owner.
          </p>
        </div>

        <RetireBoardSection board={board} onDone={onDone} />
        <BoardActivityList activity={board.activity ?? []} />
      </div>

      <DialogFooter>
        <SButton kind="ghost" onClick={onDone}>
          Cancel
        </SButton>
        <SButton kind="primary" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save"}
        </SButton>
      </DialogFooter>
    </>
  )
}

/**
 * Retiring a board: Archive first (managers and owners, undone with
 * Unarchive), Delete only as the owner's danger action below it.
 */
function RetireBoardSection({ board, onDone }: { board: Board; onDone: () => void }) {
  const [archiving, setArchiving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  if (board.is_default || !board.can_manage) return null

  return (
    <div className="space-y-3">
      <span className={rs.label}>Retire this board</span>
      <div className={cn("space-y-3 rounded-2xl border p-3", rs.rule)}>
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-medium">
              Archive board <Badge tone="info" className="ml-1 h-5 px-1.5 text-[11px]">Recommended</Badge>
            </p>
            <p className="text-xs text-muted-foreground">{ARCHIVE_BOARD_EXPLAINER}</p>
          </div>
          <SButton kind="secondary" onClick={() => setArchiving(true)}>
            <Archive className="h-4 w-4" /> Archive
          </SButton>
        </div>
        {board.can_own && (
          <div className={cn("flex flex-wrap items-start gap-3 border-t pt-3", rs.rule)}>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-medium">Delete board</p>
              <p className="text-xs text-muted-foreground">Only an empty board can be deleted. It goes to the recycle bin.</p>
            </div>
            <SButton kind="ghost" className="text-destructive hover:text-destructive dark:text-[#ff9592] dark:hover:text-[#ff9592]" onClick={() => setDeleting(true)}>
              <Trash2 className="h-4 w-4" /> Delete
            </SButton>
          </div>
        )}
      </div>
      <ArchiveBoardDialog board={board} open={archiving} onOpenChange={setArchiving} onArchived={onDone} />
      {deleting && <DeleteBoardDialog board={board} open onOpenChange={setDeleting} onDeleted={onDone} />}
    </div>
  )
}

function DeleteBoardDialog({
  board,
  open,
  onOpenChange,
  onDeleted,
}: {
  board: Board
  open: boolean
  onOpenChange: (open: boolean) => void
  onDeleted: () => void
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: () => api.boards.remove(board.key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["boards"] })
      queryClient.removeQueries({ queryKey: ["board", board.key] })
      toast({ title: `Board ${board.key} deleted` })
      onOpenChange(false)
      onDeleted()
    },
    onError: (e) => toast({ title: "Not deleted", description: apiError(e, "The board could not be deleted."), variant: "destructive" }),
  })

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {board.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            A board that still has tasks can't be deleted, so archive it instead. An empty board goes to the recycle bin, where an admin can restore it.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <SButton
            kind="primary"
            className="border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90 dark:border-destructive dark:bg-destructive dark:text-destructive-foreground"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {remove.isPending ? "Deleting…" : "Delete board"}
          </SButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/** An archived board's settings: its archived state, Unarchive, and who did what. */
function ArchivedBoardSettings({ board, onDone }: { board: Board; onDone: () => void }) {
  const when = formatArchivedDate(board.archived_at)
  return (
    <>
      <div className="space-y-6">
        <div className={cn("flex flex-wrap items-center gap-3 rounded-2xl border p-3", rs.rule)}>
          <ArchivedBoardBadge label="Archived" />
          <p className="min-w-0 flex-1 text-sm text-muted-foreground">
            {when ? `Archived on ${when}.` : "Archived."} Its tasks are read-only, and its webhooks and app tokens are paused.
          </p>
        </div>
        <BoardActivityList activity={board.activity ?? []} />
      </div>
      <DialogFooter>
        <SButton kind="ghost" onClick={onDone}>
          Close
        </SButton>
        {board.can_manage && <UnarchiveButton board={board} kind="primary" onDone={onDone} />}
      </DialogFooter>
    </>
  )
}

/** Tasks → Settings: the boards archived out of the sidebar, with Unarchive for managers. */
export function ArchivedBoardsCard() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ARCHIVED_BOARDS_QUERY_KEY,
    queryFn: () => api.boards.list("only"),
    staleTime: 30_000,
  })
  const boards = Array.isArray(data) ? data : []
  const [viewing, setViewing] = useState<string | null>(null)

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Archive className="h-4 w-4" /> Archived boards
        </span>
      }
      flush
      className="max-w-3xl"
    >
      {isLoading ? (
        <div className={cn(rs.pulse, "mx-4 mb-4 h-10 md:mx-6")} />
      ) : isError ? (
        <p className="px-4 pb-4 text-sm text-muted-foreground md:px-6">Archived boards could not be loaded.</p>
      ) : boards.length === 0 ? (
        <p className="px-4 pb-4 text-sm text-muted-foreground md:px-6">No archived boards. A board you archive moves here, out of the sidebar.</p>
      ) : (
        <ul className="pb-2">
          {boards.map((board) => (
            <li key={board.key} className={cn("flex flex-wrap items-center gap-3 border-t px-4 py-2.5 md:px-6", rs.rule)}>
              <BoardTile name={board.name} color={board.color} logoUrl={board.logo_url} size={20} />
              <span className="flex min-w-0 flex-1 items-center gap-2 truncate text-sm font-medium">
                <Link to={boardHref(board.key, "")} className="truncate hover:underline">
                  {board.name}
                </Link>
                <span className="font-mono text-[11px] text-muted-foreground">{board.key}</span>
                <BoardPrivateMark board={board} />
              </span>
              {formatArchivedDate(board.archived_at) && (
                <span className="text-xs text-muted-foreground">Archived {formatArchivedDate(board.archived_at)}</span>
              )}
              <SButton kind="small" onClick={() => setViewing(board.key)}>
                Details
              </SButton>
              {board.can_manage && <UnarchiveButton board={board} kind="small" />}
            </li>
          ))}
        </ul>
      )}
      {viewing && <BoardSettingsDialog boardKey={viewing} open onOpenChange={(open) => !open && setViewing(null)} />}
    </Card>
  )
}

function NewBoardDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [key, setKey] = useState("")
  const [name, setName] = useState("")
  const [visibility, setVisibility] = useState<BoardVisibility>("workspace")
  const keyValid = /^[A-Za-z][A-Za-z0-9]{0,9}$/.test(key)

  const create = useMutation({
    mutationFn: () => api.boards.create({ key: key.toUpperCase(), name: name.trim(), visibility }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["boards"] })
      toast({ title: `Board ${key.toUpperCase()} created` })
      onOpenChange(false)
    },
    onError: (e) => toast({ title: "Not created", description: apiError(e, "The board could not be created."), variant: "destructive" }),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>New board</DialogTitle>
          <DialogDescription>Tasks on it are numbered from its key, like {key ? key.toUpperCase() : "OPS"}-1.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <label className="block space-y-2">
            <span className={rs.label}>Key</span>
            <Input className={cn(rs.input, "font-mono uppercase")} value={key} maxLength={10} placeholder="OPS" onChange={(e) => setKey(e.target.value.trim())} />
          </label>
          <label className="block space-y-2">
            <span className={rs.label}>Name</span>
            <Input className={rs.input} value={name} maxLength={100} placeholder="Operations" onChange={(e) => setName(e.target.value)} />
          </label>
          <div className="space-y-2">
            <span className={rs.label}>Visibility</span>
            <Segments label="Visibility" value={visibility} options={VISIBILITY_OPTIONS} onChange={setVisibility} />
            <p className="text-xs text-muted-foreground">
              {VISIBILITY_HINTS[visibility]}
              {visibility === "members" ? " You become its owner; add members from its settings." : " Add members from its settings."}
            </p>
          </div>
        </div>
        <DialogFooter>
          <SButton kind="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </SButton>
          <SButton kind="primary" disabled={!keyValid || !name.trim() || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? "Creating…" : "Create board"}
          </SButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
